import { ContactKind, ManagerKind } from '@ail/customers-api'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'
import { FrontendSettings } from '../../../helpers/FrontendSettings'
import { PermissionManager } from '../../../helpers/PermissionManager'
import { useGridFilters } from '../../../hooks/useGridFilters'
import { VehicleOrderStats } from '../../../hooks/useOrderStats'
import { IUserViewModel } from '../../../types/interfaces'
import { LoadingSpinner } from '../../common/LoadingSpinner/LoadingSpinner'
import { ParseHtml } from '../../common/ParseHtml/ParseHtml'
import { OrderWarningCount } from '../../common/WarningCount/WarningCount'
import { ContractStateEnum, FrontendSettingsKeys } from './../../../types/enums'
import { ICustomerViewModel, ITenantViewModel } from './../../../types/interfaces'
import './CompanyWidget.scss'
import CompanyWidgetUnits from './CompanyWidgetUnits'

interface Props {
  tenant?: ITenantViewModel
  selectedManager?: string
  key?: number
  companyIsChild?: boolean
  company: ICustomerViewModel
  isTreeViewMode?: boolean
  // smallDisplay?: boolean
  isHomePage?: boolean
  isInternalUser?: boolean
  isAdmin?: boolean
  user?: IUserViewModel
  sendReport: () => void
  selectedCompany: string
  vehicleOrderStats?: VehicleOrderStats
  showStats?: boolean
}

function CompanyWidget({
  tenant,
  selectedManager,
  key,
  companyIsChild,
  company,
  isTreeViewMode,
  isHomePage,
  isInternalUser,
  isAdmin,
  user,
  sendReport,
  selectedCompany,
  vehicleOrderStats,
  showStats,
}: Props) {
  const [showUnits, setShowUnits] = useState(false)
  const readValue = (v?: string) => (v ? v : '-')
  const { t } = useTranslation('common')
  const [updateFilterValue] = useGridFilters()
  // const [getPersonsInfo, personsInfo] = useLazyGetCustomerPersonsQuery()
  const contracts = company.contracts
  const mainStats = vehicleOrderStats?.[company.id]

  const handleDeliveryRedirect = () => {
    if (mainStats?.actionRequired === 0) return
    updateFilterValue({ gridId: 'Delivery', newFilters: { requestLink: true } })
  }
  const newStats = {
    total: 0,
    active: 0,
    closed: 0,
    initialized: 0,
  }
  if (contracts) {
    const statusToCount = [
      ContractStateEnum.Closed,
      ContractStateEnum.Running,
      ContractStateEnum.Terminated,
      ContractStateEnum.Extended,
      ContractStateEnum.Initialized,
    ]

    const statusForActive = [ContractStateEnum.Running, ContractStateEnum.Extended]

    const statusForClosed = [ContractStateEnum.Closed, ContractStateEnum.Terminated]

    const statusForInitialized = [ContractStateEnum.Initialized]

    statusToCount.forEach((status) => {
      newStats.total += contracts[status] || 0
    })

    statusForActive.forEach((status) => {
      newStats.active += contracts[status] || 0
    })

    statusForClosed.forEach((status) => {
      newStats.closed += contracts[status] || 0
    })

    statusForInitialized.forEach((status) => {
      newStats.initialized += contracts[status] || 0
    })
  }

  const [showInfo, setShowInfo] = useState(false)

  const [companyTreeViewMode, setCompanyTreeViewMode] = useState(false)

  const defaultPhoneContacts = company.primary?.contacts?.filter((c) => c.kind === ContactKind.PhoneNumber)
  const defaultEmailContacts = company.primary?.contacts?.filter(
    (c) => c.kind === ContactKind.CorrespondenceEmail && c.value?.includes('@')
  )

  const companyHasSelectedChild = company.children?.find((child) => child.id === selectedCompany)

  const currentCompanyIsSelected = company.id === selectedCompany
  useEffect(() => {
    if (!companyIsChild) {
      setCompanyTreeViewMode(true)
    }

    if (companyIsChild && (companyHasSelectedChild || currentCompanyIsSelected)) {
      setCompanyTreeViewMode(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyIsChild])

  useEffect(() => {
    if (showInfo) {
      setShowInfo(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedManager])

  function switchInfoDisplay() {
    //*For manual Primary data fetch, when old customer aggregation will be changed
    // if (!showInfo && !personsInfo.data) {
    //   getPersonsInfo({ customerId: company.id, roles: [PersonRole.Primary] })
    // }
    setShowInfo(!showInfo)
  }

  function changeCompanyTreeViewMode() {
    setCompanyTreeViewMode(!companyTreeViewMode)
  }

  function toggleUnitsGrid() {
    setShowUnits(!showUnits)
  }

  const companyHasTree = (company.children?.length ?? 0) > 0 || (company.parents?.length ?? 0) > 0
  const personsAddressInfo = company.primary?.addresses
  const basePath = isInternalUser ? 'dashboard' : 'dashboard-home'
  const dashboardPath = `/${basePath}/${company.id}`
  const treeviewPath = `${dashboardPath}/treeview`
  return (
    <>
      <div className={companyIsChild ? '' : 'companyArea'}>
        <div
          className={`box-title-company ${isTreeViewMode && currentCompanyIsSelected ? 'isSelected' : ''}`}
          title={company.name}
        >
          <NavLink to={`/generalContracts/${company.id}`}>{company.name}</NavLink>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {showStats && mainStats && (
              <NavLink onClick={handleDeliveryRedirect} to={`/orderAndDelivery/${company.id}`} className="orderWarning">
                <OrderWarningCount
                  showOrderWarningCount={true}
                  totalActionRequired={mainStats.actionRequired}
                  totalFrozenOrders={mainStats.frozen}
                />
              </NavLink>
            )}
            {user?.isInternalUser && companyHasTree && (
              <NavLink title="Group dashboard" className="dashBtn" to={treeviewPath}>
                <i className="icon-assign" />
              </NavLink>
            )}
            <NavLink title="Company dashboard" className="dashBtn" to={dashboardPath}>
              <i className="icon-statistics"></i>
            </NavLink>{' '}
            <i onClick={() => switchInfoDisplay()} className="icon-info dashBtn" />
          </div>
        </div>
        {showInfo && (
          <div className="box2 white">
            <div className="row">
              {user?.isInternalUser && tenant?.name && (
                <div className="col-6">
                  <i className="icon-4u" />
                  <span>{tenant?.name}</span>
                </div>
              )}
              {personsAddressInfo || defaultEmailContacts || defaultPhoneContacts ? (
                <>
                  <div className="col-6">
                    <i className="icon-id" />
                    <span>{readValue(company.reference)}</span>
                  </div>

                  {personsAddressInfo?.length && (
                    <>
                      <div className="col-6">
                        <i className="icon-marker" />
                        <span>
                          <ParseHtml html={personsAddressInfo[0]?.street} />,{' '}
                          <ParseHtml html={personsAddressInfo[0]?.postalCode} />{' '}
                          <ParseHtml html={personsAddressInfo[0]?.city} />
                        </span>
                      </div>

                      <div className="col-6">
                        <i className="icon-vat" />
                        <span>{readValue(company.vatNumber)}</span>
                      </div>
                    </>
                  )}
                  {defaultEmailContacts?.length ? (
                    <div className="col-6">
                      <i className="icon-mail" />
                      <span className="contact">
                        <a
                          href={`mailto:${readValue(defaultEmailContacts[0]?.value)}`}
                          title={readValue(defaultEmailContacts[0]?.value)}
                        >
                          {readValue(defaultEmailContacts[0]?.value)}
                        </a>
                      </span>
                    </div>
                  ) : (
                    defaultPhoneContacts &&
                    defaultPhoneContacts?.length > 0 && (
                      <div className="col-6">
                        <i className="icon-phone" />
                        <span className="contact">
                          <a
                            href={`tel:${readValue(defaultPhoneContacts[0]?.value)}`}
                            title={readValue(defaultPhoneContacts[0]?.value)}
                          >
                            {readValue(defaultPhoneContacts[0]?.value)}
                          </a>
                        </span>
                      </div>
                    )
                  )}
                </>
              ) : (
                <div style={{ marginLeft: 'auto', marginRight: 'auto' }}>
                  <LoadingSpinner size={15} />
                </div>
              )}
            </div>
          </div>
        )}
        <div className="box-footer">
          <div className="boxFooterA">
            {!isHomePage && user?.isInternalUser && isTreeViewMode && (
              <i
                onClick={() => company.children?.length && changeCompanyTreeViewMode()}
                className={`icon-assign viewBtn ${
                  (company.children?.length === 0 || company.children === undefined) && 'disabled'
                } ${company.children?.length && companyTreeViewMode && 'active'}`}
              />
            )}
            {!isHomePage && user?.isInternalUser && !isTreeViewMode && companyHasTree && (
              <NavLink title="Group dashboard" className="viewBtn" to={`/dashboard/${company.id}/treeview`}>
                <i className="icon-assign" />
              </NavLink>
            )}
            <div className="summaryArea">
              <div className="summaryC activeC">
                <i className="icon-car" />
                {newStats.active}
              </div>
              {/* <div className="summaryC closedC">
                    <i className="icon-closed" />
                    {newStats.closed}
                  </div> */}
              <div className="summaryC pendingC">
                <i className="icon-pending" />
                {newStats.initialized}
              </div>
            </div>
          </div>
          <div className="footerLink">
            {user?.isInternalUser &&
              (company.units?.filter((q) => q.managers?.some((m) => m.kind === ManagerKind.FleetManager))?.length ??
                0) > 0 && (
                <a onClick={() => toggleUnitsGrid()} title={t('UNITS')}>
                  <i className="icon-units" />
                </a>
              )}
            {user?.isInternalUser && !!company.totalContracts && (
              <NavLink title={t('REPORTS')} onClick={() => sendReport()} to={''}>
                <i className="icon-reports-man" />
              </NavLink>
            )}
            <NavLink title={t('SEE_CONTRACTS')} to={`/generalContracts/${company.id}`}>
              <i className="icon-contract" />
            </NavLink>
            <NavLink
              className={newStats.initialized <= 0 ? 'disabled' : undefined}
              title={t('ORDER_AND_DELIVERY')}
              to={`/orderAndDelivery/${company.id}`}
            >
              <i className="icon-garageInOut" />
            </NavLink>
            {user && PermissionManager.canViewCosts(user) && (
              <NavLink
                title={t('DELTA_VIEW')}
                to={`/deltaContracts${
                  FrontendSettings.get(FrontendSettingsKeys.deltaContractsMode) === 'old' ? 'Old' : ''
                }/${company.id}`}
              >
                <i className="icon-delta" />
              </NavLink>
            )}
          </div>
        </div>
        {companyTreeViewMode &&
          isTreeViewMode &&
          (company.children?.length ?? 0) > 0 &&
          company.children?.map((el, i) => (
            <div className={`${key === 0 ? 'lineForTreeviewFirst companyArea' : 'lineForTreeview companyArea'}`}>
              <CompanyWidget
                companyIsChild={true}
                key={i}
                isTreeViewMode={isTreeViewMode}
                company={el}
                // smallDisplay={smallDisplay}
                isHomePage={isHomePage}
                isInternalUser={isInternalUser}
                isAdmin={isAdmin}
                user={user}
                sendReport={sendReport}
                selectedCompany={selectedCompany}
                showStats={showStats}
                vehicleOrderStats={vehicleOrderStats}
              />
            </div>
          ))}
      </div>
      {showUnits && (
        <CompanyWidgetUnits
          onClose={() => {
            toggleUnitsGrid()
          }}
          customer={company}
          units={company.units}
          visible={showUnits}
          //@ts-expect-error Non-refactored code below, TS errors expected
          location={undefined}
          //@ts-expect-error Non-refactored code below, TS errors expected
          history={undefined}
          //@ts-expect-error Non-refactored code below, TS errors expected
          match={undefined}
        />
      )}
    </>
  )
}

export default CompanyWidget
